import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ShieldAlert, Loader2, Database } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// Request Access popup for Ask AI's PII-blocked answers. Mirrors the
// PII & Data Access → Request Access screen: the semantic model is shown
// pre-chosen (read-only, driven by the query) and the PII field(s) that the
// question needed are pre-selected. The user reviews the fields + reason and
// submits a temporary-unmask request for admin approval.
export default function RequestPiiAccessDialog({ open, onOpenChange, modelId, preselectedFields = [], question = '', onSubmitted }) {
  const [modelName, setModelName] = useState('');
  const [piiFields, setPiiFields] = useState([]);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState('');
  const [loadingModel, setLoadingModel] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const normalize = useCallback((f) => (f || '').toString().toUpperCase(), []);

  useEffect(() => {
    if (!open || !modelId) return;
    setLoadingModel(true);
    // Resolve the model's friendly name for the read-only label.
    api.getSemanticModels()
      .then((models) => {
        const m = (models || []).find((x) => (x.model_id || x.id) === modelId);
        setModelName(m?.name || m?.display_name || modelId);
      })
      .catch(() => setModelName(modelId));
    // Load every PII field on the model so the user can adjust, then
    // pre-select the one(s) that were flagged in their question (case-insensitive).
    api.getSemanticModelFull(modelId)
      .then((m) => {
        const fields = [];
        (m.entities || []).forEach((e) => (e.attributes || []).forEach((a) => {
          if (a.pii && a.name && !fields.includes(a.name)) fields.push(a.name);
        }));
        // Make sure any flagged field the model no longer lists is still offered.
        const preNorm = preselectedFields.map(normalize);
        preselectedFields.forEach((pf) => {
          if (!fields.some((f) => normalize(f) === normalize(pf))) fields.push(pf);
        });
        setPiiFields(fields);
        setSelected(fields.filter((f) => preNorm.includes(normalize(f))));
      })
      .catch(() => {
        // Fall back to just the flagged fields if the model can't be loaded.
        setPiiFields(preselectedFields);
        setSelected(preselectedFields);
      })
      .finally(() => setLoadingModel(false));
    setReason(question ? `Requested via Ask AI to answer: "${question}"` : '');
  }, [open, modelId, preselectedFields, question, normalize]);

  const toggle = (f) => setSelected((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));

  const submit = async () => {
    if (!modelId || selected.length === 0) { toast.error('Select at least one field'); return; }
    setSubmitting(true);
    try {
      await api.createUnmaskRequest({ model_id: modelId, fields: selected, reason });
      toast.success('Access request sent — an admin will review it');
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Could not send access request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="request-pii-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-rose-400" /> Request PII access
          </DialogTitle>
          <DialogDescription>
            Request temporary (2-hour) unmasked access to the PII field(s) this question needs. An admin must approve.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Semantic model</Label>
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-border/50 bg-muted/30 px-3 py-2 text-sm" data-testid="request-pii-model">
              <Database className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              {loadingModel && !modelName ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : <span className="text-foreground truncate">{modelName || modelId}</span>}
            </div>
          </div>

          <div>
            <Label className="text-xs">PII fields</Label>
            {loadingModel ? (
              <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Loading fields…</div>
            ) : piiFields.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No PII fields marked on this model.</p>
            ) : (
              <div className="flex flex-wrap gap-2 mt-1">
                {piiFields.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => toggle(f)}
                    data-testid={`request-pii-field-${f}`}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${selected.includes(f) ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 hover:border-primary/40'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why do you need unmasked access?"
              className="mt-1"
              data-testid="request-pii-reason"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="request-pii-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting || selected.length === 0} className="gap-2" data-testid="request-pii-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />} Request access
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
